import { Injectable, inject, signal } from '@angular/core';
import { formatUtcTime } from './error-text';
import { REQUEST_ID_HEADER, ThirdPartyIntegrationsService } from './third-party-integrations.service';

//
// The language, the kind (article, open question or answered question) and the answer of a tip are what
// the window shows next to its title; a tip of the service without them is an article in no language.
//
export interface HelpTip
{
  readMoreUrl: string;
  title: string;
  languageCode: string;
  isQuestion: boolean;
  hasAnswer: boolean;
}

//
// Structurally typed rather than Angular's HttpErrorResponse, matching what showFromHttpError below
// already does: this service is about what is on screen, and keeping it independent of the HTTP layer
// means it can be driven from a plain object without constructing a framework class.
//
export interface HttpErrorResponseLike
{
  status: number;
  url?: string | null;
  error?: unknown;
  headers?: { get(name: string): string | null };
}

export interface ErrorModalState
{
  visible: boolean;
  title: string;
  message: string;
  messageCode: string;
  naturalKey: string | null;
  showNaturalKeyWrapper: boolean;
  uid: string;
  showUidWrapper: boolean;
  kindRequest: string;
  showKindRequest: boolean;
  showHelpTips: boolean;
  helpTips: HelpTip[];

  //
  // Which request failed, and how. Added 2026-08-14: the modal used to name the error but not what
  // provoked it, so from a browser there was no way to tell a slow backend from one answering the
  // wrong content type from a frontend defect - the three need different people to fix them. The
  // natural-key row carried the query string only, which identifies the parameters but not the
  // endpoint or the status.
  //
  requestUrl: string | null;
  requestStatus: number | null;
  showRequestWrapper: boolean;

  //
  // The request ID is what support asks for, and the time and the recoverable flag are what the service
  // reports with its error. A second attempt is only offered when the service says one can solve it.
  //
  requestId: string;
  timeOccurredUtc: string;
  isRecoverable: boolean;
}

//
// The wrapper flags default to false because a label with nothing behind it is worse than no label:
// the original's markup had these rows visible by default and hid them again from jQuery, which meant
// an error carrying no natural key still rendered a bare "Natural Key:" line. Defaulting to hidden and
// switching them on only when there is a value produces the same result for errors that DO carry one,
// without that empty row.
//
const CLOSED_ERROR_MODAL: ErrorModalState =
{
  visible: false
, title: ''
, message: ''
, messageCode: ''
, naturalKey: null
, showNaturalKeyWrapper: false
, uid: ''
, showUidWrapper: false
, kindRequest: ''
, showKindRequest: true
, showHelpTips: false
, helpTips: []
, requestUrl: null
, requestStatus: null
, showRequestWrapper: false
, requestId: ''
, timeOccurredUtc: ''
, isRecoverable: false
};

//
// Ports the two independent DOM-writing paths from the AngularJS app, unchanged in spirit:
// - errorInterceptor.responseError (gmr-invantive.js) fires globally for every failed $http call.
// - showHttpGetError (gmr-app.js) is called explicitly from specific .catch() handlers with a msgCode.
// Both run for a typical failed request and both write into the same modal - each only touches the
// fields it originally touched via jQuery, so partial merges here reproduce the same "last writer
// wins per-field" behaviour the original selective DOM writes had.
//
@Injectable({ providedIn: 'root' })
export class ErrorStateService
{
  private readonly thirdParty = inject(ThirdPartyIntegrationsService);

  readonly errorModal = signal<ErrorModalState>(CLOSED_ERROR_MODAL);

  //
  // The tips asked for so far, per request: the page shows its message again on a change of the language, and the second time the
  // tips are at hand at once, or the lookup which is still on its way is waited for. A lookup which failed is forgotten.
  //
  private readonly tipsByRequest = new Map<string, Promise<HelpTip[] | null>>();

  showFromInterceptor(errorData: any, errorLabel: string, requestIdOfResponse?: string | null): void
  {
    const naturalKey: string | undefined = errorData?.NaturalKey;
    const kindRequest: string = errorData?.KindRequest ?? '';
    const helpTips: HelpTip[] = !isEmptyObject(errorData?.OnlineHelpTips) ? toHelpTips(errorData.OnlineHelpTips) : [];

    this.errorModal.update(current => (
    {
      ...current
    , visible: true
    , title: `${errorLabel} ${errorData?.MessageCode ?? ''}`.trim()
    , message: errorData?.Message ?? ''
    , messageCode: errorData?.MessageCode ?? ''
    , kindRequest
    , showKindRequest: kindRequest !== ''
    , uid: errorData?.Uid ?? ''
    , showUidWrapper: !!errorData?.Uid
    , naturalKey: naturalKey ? naturalKey : current.naturalKey
    , showNaturalKeyWrapper: !!naturalKey
    , showHelpTips: helpTips.length > 0 ? true : current.showHelpTips
    , helpTips: helpTips.length > 0 ? helpTips : current.helpTips
    , requestId: errorData?.RequestId || requestIdOfResponse || current.requestId
    , timeOccurredUtc: formatUtcTime(errorData?.TimeOccurredUtc)
    , isRecoverable: errorData?.IsRecoverable === true
    }));
  }

  //
  // 1:1 port of showHttpGetError(error, msgCode) in gmr-app.js.
  //
  showFromHttpError(error: { status?: number; statusText?: string | null; url?: string | null; headers?: { get(name: string): string | null } } | string, msgCode: string, translations:
  {
    itgen_gmr_error: string;
    itgen_gmr_failed_data_retrieval: string;
    itgen_gmr_reauthenticate: string;
  }
  , hint?: string): void
  {
    const hasStatus = typeof error === 'object' && !!error.status;

    let naturalKeyCustomError: string | undefined;
    let message: string;
    let showNaturalKeyWrapper: boolean;

    if (hasStatus && typeof error === 'object')
    {
      message = `${translations.itgen_gmr_failed_data_retrieval} ${error.status}: ${error.statusText}.`;
      naturalKeyCustomError = error.url?.split('?').slice(1).join('?');
      showNaturalKeyWrapper = !!naturalKeyCustomError;
    }
    else
    {
      message = String(error);
      showNaturalKeyWrapper = false;
    }

    let title = `${translations.itgen_gmr_error} ${msgCode}`;

    //
    // Special-case: session expired while requesting a preset.
    //
    if (msgCode === 'xxgmr115' && typeof error === 'object' && error.url?.startsWith('Preset?preset='))
    {
      title = `${translations.itgen_gmr_error} ${msgCode}`;
      message = translations.itgen_gmr_reauthenticate;
      naturalKeyCustomError = 'xxgmr115-notauthenticated';
    }

    const requestUrl = typeof error === 'object' ? (error.url ?? null) : null;
    const requestStatus = typeof error === 'object' ? (error.status ?? null) : null;

    this.errorModal.update(current => (
    {
      ...current
    , visible: true
    , title
    , message
    , messageCode: msgCode
    , naturalKey: naturalKeyCustomError ?? current.naturalKey
    , showNaturalKeyWrapper
    , kindRequest: hint ?? ''
    , showKindRequest: !!hint
    , showUidWrapper: false
    , requestUrl
    , requestStatus
    , showRequestWrapper: requestUrl != null
    , requestId: (typeof error === 'object' ? error.headers?.get(REQUEST_ID_HEADER) : null) || current.requestId
    }));

    //
    // showHttpGetError reports "<message> (<naturalKey>)" to Matomo/Inspectlet, using whatever
    // .message text is on screen at that point (read back from the DOM in the original - here
    // the value we just computed is equivalent since nothing else writes this field concurrently).
    //
    // The natural key is omitted when there is none, rather than interpolated: the original appends
    // it unconditionally and so files half its telemetry under the literal text "(undefined)".
    //
    this.thirdParty.reportHttpError(naturalKeyCustomError ? `${message} (${naturalKeyCustomError})` : message, msgCode, typeof error === 'object' ? error.headers?.get(REQUEST_ID_HEADER) : null);

    this.requestTips(msgCode);
  }

  //
  // A response that succeeded at the HTTP level but did not carry JSON. See the reasoning in
  // GmrDataService.handleFallbackError for why this needed its own path: the generic guard there read
  // the parse failure sitting in `.error` as proof that something else had already reported the
  // problem, and the result was an application that hung in silence rather than one that said what
  // went wrong.
  //
  // It gets its own method rather than a flag on showFromHttpError because what is worth showing is
  // different. There is no status text worth repeating - the status is 200, which is precisely the
  // confusing part - so the status is shown next to the URL under "request", and the message says the
  // server's answer could not be read. The first part of the body is included as the natural key: an
  // HTML login page, a gateway notice and a truncated JSON document are immediately distinguishable
  // by eye, and that single line usually settles who has to fix it.
  //
  showFromParseFailure(error: HttpErrorResponseLike, msgCode: string, errorLabel: string, invalidAnswerMessage: string): void
  {
    //
    // HttpClient's XHR backend wraps the failure as `{ error: SyntaxError, text: <original body> }`;
    // its fetch backend does not necessarily. Read defensively and fall back to nothing rather than
    // printing "[object Object]" at the user.
    //
    const wrapper = error.error as { text?: unknown } | null | undefined;
    const bodyText = typeof wrapper?.text === 'string' ? wrapper.text : null;
    const bodyExcerpt = bodyText ? bodyText.replace(/\s+/g, ' ').trim().slice(0, 120) : null;

    const message = `${invalidAnswerMessage} HTTP ${error.status}, body is not JSON.`;

    this.errorModal.update(current => (
    {
      ...current
    , visible: true
    , title: `${errorLabel} ${msgCode}`
    , message
    , messageCode: msgCode
    , naturalKey: bodyExcerpt ?? current.naturalKey
    , showNaturalKeyWrapper: bodyExcerpt != null
    , showKindRequest: false
    , showUidWrapper: false
    , requestUrl: error.url ?? null
    , requestStatus: error.status
    , showRequestWrapper: true
    , requestId: error.headers?.get(REQUEST_ID_HEADER) || current.requestId
    }));

    this.thirdParty.reportHttpError(`${message} (${error.url ?? 'no url'})`, msgCode, error.headers?.get(REQUEST_ID_HEADER));

    this.requestTips(msgCode);
  }

  //
  // A code which the page makes itself, such as xxgmr114, is no answer of the Data Access Point and so comes without the tips of the
  // forums which an error of the server has. They are asked from the Data Access Point once the message is shown and added when they
  // arrive, as the first lookup of a code can take seconds. The call is made with fetch and not with HttpClient: the error interceptor
  // treats every failed call as fatal and switches further requests off, and a lookup which fails must leave the message as it is.
  // Not for the notice of a lost session: the lookup needs that session.
  //
  private requestTips(messageCode: string): void
  {
    if (!messageCode || messageCode === 'xxgmr115' || typeof fetch !== 'function')
    {
      return;
    }

    const url = `Preset?preset=gmr-tips&message_code=${encodeURIComponent(messageCode)}`;

    let tips = this.tipsByRequest.get(url);

    if (!tips)
    {
      tips = fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
        .then(response => response.ok ? response.json() : null)
        .then(data => Array.isArray(data?.Results?.tips?.Data) ? toHelpTipsFromRows(data.Results.tips.Data) : null)
        .catch(() => null)
        .then(result =>
        {
          if (result === null)
          {
            this.tipsByRequest.delete(url);
          }

          return result;
        });

      this.tipsByRequest.set(url, tips);
    }

    //
    // The message may have been closed, or replaced by the message of another code, while the lookup ran. Without tips the message says
    // what it has to say.
    //
    void tips.then(result =>
    {
      if (result && result.length > 0)
      {
        this.errorModal.update(current => current.visible && current.messageCode === messageCode
          ? { ...current, showHelpTips: true, helpTips: result }
          : current);
      }
    });
  }

  closeError(): void
  {
    this.errorModal.set(CLOSED_ERROR_MODAL);
  }
}

function isEmptyObject(value: unknown): boolean
{
  return value == null || (typeof value === 'object' && Object.keys(value).length === 0);
}

//
// Release 27.0 of the service names the fields Title and ReadMoreUrl; earlier releases sent them in camel
// case, and either arrives as a list or as a map keyed by index. A tip without a title would show as an
// empty link, and one without a web address as no link at all, so both are left out.
//
function toHelpTipsFromRows(rows: Record<string, unknown>[]): HelpTip[]
{
  //
  // The casing of a field of a row depends on how the template names it.
  //
  return toHelpTips(rows.map(row =>
  {
    const lower = Object.fromEntries(Object.entries(row).map(([key, value]) => [key.toLowerCase(), value]));

    return { readMoreUrl: lower['readmoreurl'], title: lower['title'], languageCode: lower['languagecode'], isQuestion: lower['isquestion'], hasAnswer: lower['hasanswer'] };
  }));
}

function toHelpTips(value: Record<string, any>): HelpTip[]
{
  return Object.values(value)
    .map(tip => (
    {
      readMoreUrl: String(tip?.ReadMoreUrl ?? tip?.readMoreUrl ?? '')
    , title: String(tip?.Title ?? tip?.title ?? '')
    , languageCode: String(tip?.LanguageCode ?? tip?.languageCode ?? '').substring(0, 2).toLowerCase()
    , isQuestion: (tip?.IsQuestion ?? tip?.isQuestion) === true
    , hasAnswer: (tip?.HasAnswer ?? tip?.hasAnswer) === true
    }))
    .filter(tip => tip.title !== '' && /^https?:\/\//i.test(tip.readMoreUrl));
}
